import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useBooking } from "@/lib/booking-context";
import { IMAGES } from "@/data/content";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.15, delay: 0.55 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
});

const trust = ["Fully Insured", "Background-Checked Professionals", "Satisfaction Guaranteed", "Luxury Homes & Estates"];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const { openBooking } = useBooking();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 150]);
  const overlayFade = useTransform(scrollY, [0, 500], [1, 0.85]);

  const lines = ["Luxury Residential Cleaning,", "Designed Around Your Home."];

  return (
    <section className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      {/* Image + Ken Burns zoom */}
      <motion.div style={{ y }} className="absolute -inset-y-[8%] inset-x-0 will-change-transform">
        <motion.img
          src={IMAGES.hero}
          alt="Luxury modern home at dusk"
          className="w-full h-full object-cover"
          initial={{ scale: 1.0 }}
          animate={{ scale: 1.045 }}
          transition={{ duration: 14, ease: "easeOut" }}
        />
      </motion.div>

      {/* Layered overlay system */}
      <motion.div style={{ opacity: overlayFade }} className="absolute inset-0 pointer-events-none">
        {/* top 60% · middle 40% · bottom 65% */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/[0.66]" />
        {/* darken toward the text (bottom-left) */}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
        {/* subtle vignette */}
        <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 200px 60px rgba(0,0,0,0.55)" }} />
      </motion.div>

      {/* Localized blur behind headline only (fades upward) */}
      <div
        className="absolute bottom-0 left-0 w-full lg:w-[68%] h-[62%] backdrop-blur-[2.5px] pointer-events-none"
        style={{
          maskImage: "linear-gradient(to top, black 35%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to top, black 35%, transparent 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 sm:px-8 md:px-12 flex flex-col justify-end pb-12 sm:pb-20 md:pb-28">
        <motion.p {...fadeUp(0.35)} className="overline text-champagne mb-4 sm:mb-6 md:mb-8">
          Concierge Home Care
        </motion.p>

        <h1 className="font-serif text-warmwhite font-light tracking-tight leading-[1.12] md:leading-[1.05] text-[1.85rem] min-[360px]:text-[2.1rem] min-[400px]:text-[2.35rem] sm:text-5xl lg:text-7xl max-w-[16ch] lg:max-w-5xl [text-wrap:balance]">
          {lines.map((ln, i) => (
            <span key={i} className="block overflow-hidden pb-[0.06em]">
              <motion.span custom={i} variants={line} initial="hidden" animate="visible" className="block">
                {ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          {...fadeUp(1.15)}
          className="mt-4 sm:mt-6 md:mt-7 max-w-[34ch] md:max-w-md text-warmwhite/85 text-[15px] sm:text-base md:text-lg leading-relaxed"
        >
          Trusted professionals and concierge-level care — so your home stays effortlessly pristine.
        </motion.p>

        <motion.div {...fadeUp(1.35)} className="mt-6 sm:mt-7 md:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={openBooking}
            className="w-full sm:w-auto px-9 py-4 bg-warmwhite text-charcoal rounded-full text-sm font-semibold tracking-wide shadow-[0_10px_34px_rgba(0,0,0,0.28)] transition-[transform,background-color,box-shadow,color] duration-300 hover:bg-champagne hover:text-warmwhite hover:shadow-[0_14px_44px_rgba(197,165,114,0.45)] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.97]"
            data-testid="hero-schedule-btn"
          >
            Schedule Your Cleaning
          </button>
          <button
            onClick={() => scrollTo("experiences")}
            className="w-full sm:w-auto px-9 py-4 border border-warmwhite/45 text-warmwhite rounded-full text-sm font-semibold tracking-wide backdrop-blur-sm transition-[transform,background-color,border-color] duration-300 hover:bg-warmwhite/10 hover:border-warmwhite active:scale-[0.97]"
            data-testid="hero-explore-btn"
          >
            Explore Our Services
          </button>
        </motion.div>

        {/* Trust row */}
        <motion.ul
          {...fadeUp(1.55)}
          className="mt-6 sm:mt-9 md:mt-11 flex flex-wrap items-center gap-x-5 gap-y-2 md:gap-x-7"
          data-testid="hero-trust-row"
        >
          {trust.map((t, i) => (
            <li key={t} className="flex items-center gap-x-5 md:gap-x-7">
              {i > 0 && <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-champagne/70" aria-hidden />}
              <span className="text-warmwhite/75 text-[11px] md:text-xs tracking-[0.18em] uppercase font-medium">{t}</span>
            </li>
          ))}
        </motion.ul>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          className="w-px h-12 bg-warmwhite/50"
        />
      </motion.div>
    </section>
  );
};
